import { EllipsisHorizontalIcon, XMarkIcon } from '@heroicons/react/24/outline';
import * as React from 'react';
import { createPortal } from 'react-dom';
import Draggable, { DraggableBounds } from 'react-draggable';

import { ReactComponent as EllipsisVerticalTwoRowsIcon } from '@/assets/icons/ellipsis-vertical-two-rows.svg';
import { Button } from '@/components/core/Button';
import { ButtonDropdown } from '@/components/core/ButtonDropdown/ButtonDropdown';
import ButtonPanel from '@/components/core/ButtonPanel/ButtonPanel';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuPortal,
    DropdownMenuTrigger,
} from '@/components/core/DropdownMenu/DropdownMenu';
import { ListAction } from '@/components/core/ListAction';
import { LOCAL_STORAGE } from '@/constants';
import { useTranslation } from '@/hooks/useTranslation';
import { cn } from '@/services/utils';

import {
    DEFAULT_TOOLBAR_COUNT_LIMIT,
    formatToolbarCount,
    MAX_INLINE_TOOLBAR_ACTIONS,
    resolveInlineActionCount,
    resolveToolbarCounterMode,
    resolveToolbarMenuPlacement,
    ToolbarCounterMode,
    ToolbarMenuSide,
} from './toolbarLayout';

/** Same gray surface, white inner edge, and outer radius as the toolbar bar. */
const TOOLBAR_SURFACE_STYLE: React.CSSProperties = {
    background:
        'linear-gradient(var(--gray-100), var(--gray-100)) padding-box, ' +
        'linear-gradient(to bottom, #fff, var(--gray-100)) border-box',
    border: '1px solid transparent',
};

type ToolbarIcon = React.ComponentType<React.ComponentPropsWithoutRef<'svg'> & { title?: string }>;

export interface ToolbarMenuRow {
    id: string;
    icon?: ToolbarIcon;
    /** Rendered instead of `icon` when the glyph is not a single SVG component. */
    leading?: React.ReactNode;
    label: React.ReactNode;
    /** Full text. When set, the label stays on one line and this is the tooltip. */
    title?: string;
    disabled?: boolean;
    onClick: () => void;
}

export interface ToolbarAction {
    id: string;
    icon?: ToolbarIcon;
    label: React.ReactNode;
    disabled?: boolean;
    onClick?: () => void;
    /** When set, the action opens this list instead of calling `onClick`. */
    menu?: ToolbarMenuRow[];
    /** Extra classes for the menu panel, for example a max width so labels truncate. */
    menuClassName?: string;
    menuDividers?: boolean;
    variant?: 'ghost' | 'ghostDestructive';
}

export interface ToolbarTableLabels {
    close?: React.ReactNode;
    /**
     * Full-sentence renderer for “[N] of [M] selected”.
     * Bolding of the numbers is the caller's responsibility.
     */
    partialSelected?: (selectedCount: number, totalCount: number) => React.ReactNode;
    /**
     * Rendered when every entry is selected and the result is within the count limit.
     * Bolding of the total is the caller's responsibility.
     */
    allSelected?: (total: number) => React.ReactNode;
    /** “[N] from over [limit] selected”. */
    overLimitSelected?: (selectedCount: number, limit: number) => React.ReactNode;
    /** “All entries selected”. */
    allEntriesSelected?: () => React.ReactNode;
}

export interface ToolbarTableProps {
    selectedCount: number;
    totalCount: number;
    /**
     * Caller-supplied actions. The toolbar does not invent Select all or Delete.
     * At most five stay inline; the rest overflow into the ellipsis, with the
     * last action that overflowed as the first menu row.
     */
    actions?: ToolbarAction[];

    /**
     * Called when the user clicks the close (X) button. The toolbar hides
     * until `selectedCount` or `visible` changes, and it does not delete
     * entries. When omitted, the close button is not rendered.
     */
    onClose?: () => void;

    visible?: boolean;
    boundsRef?: React.RefObject<HTMLElement>;
    defaultPosition?: { x: number; y: number };

    /**
     * - `fixed` (default): the toolbar floats at the bottom of the viewport.
     * - `absolute`: the toolbar is positioned at the bottom of its nearest
     *   positioned ancestor.
     */
    position?: 'fixed' | 'absolute';

    labels?: ToolbarTableLabels;

    /**
     * Switches the counter to the “All [M] selected” variant. Typically on
     * when every filtered row is selected.
     */
    allSelected?: boolean;

    /**
     * Switches the counter to the over-limit copy: “[N] from over [limit]
     * selected”, or “All entries selected” when `allSelected` is also set.
     */
    overCountLimit?: boolean;

    /** Number shown in the over-limit counter. Defaults to 10,000. */
    countLimit?: number;

    className?: string;
}

const DRAG_HANDLE_CLASS = 'toolbar-table-drag-handle';
const MENU_ROW_FALLBACK_PX = 40;

const Divider = () => (
    <div className="tw-w-px tw-h-5 tw-bg-[var(--gray-350)] tw-shrink-0" aria-hidden />
);

function horizontalChrome(element: HTMLElement) {
    const style = getComputedStyle(element);
    return (
        parseFloat(style.paddingLeft)
        + parseFloat(style.paddingRight)
        + parseFloat(style.borderLeftWidth)
        + parseFloat(style.borderRightWidth)
    );
}

function readGap(element: HTMLElement | null) {
    if (!element) return 0;
    const style = getComputedStyle(element);
    const raw = style.columnGap || style.gap;
    const value = parseFloat(raw);
    return Number.isFinite(value) ? value : 0;
}

function translateOr(
    t: (key: string, values?: Record<string, string | number>) => string,
    key: string,
    values: Record<string, string | number> | undefined,
    fallback: string,
) {
    const translated = t(key, values);
    return translated === key ? fallback : translated;
}

type CounterPiece = { kind: 'text' | 'number'; value: string };

function collectCounterPieces(text: string, numbers: string[]): CounterPiece[] {
    if (numbers.length === 0 || text.length === 0) {
        const trimmed = text.trim();
        return trimmed ? [{ kind: 'text', value: trimmed }] : [];
    }
    const [current, ...rest] = numbers;
    const index = current ? text.indexOf(current) : -1;
    if (!current || index < 0) return collectCounterPieces(text, rest);
    const before = text.slice(0, index).trim();
    const after = text.slice(index + current.length);
    return [
        ...(before ? [{ kind: 'text' as const, value: before }] : []),
        { kind: 'number' as const, value: current },
        ...collectCounterPieces(after, rest),
    ];
}

function boldNumbers(text: string, numbers: string[]): React.ReactNode {
    const pieces = collectCounterPieces(text, numbers);
    if (pieces.length === 0) return null;
    const lastTextIndex = pieces.reduce(
        (found, piece, index) => (piece.kind === 'text' ? index : found),
        -1,
    );
    return pieces.map((piece, index) => {
        if (piece.kind === 'number') {
            return (
                <strong key={`${piece.value}-${index}`} className="tw-shrink-0 tw-whitespace-nowrap tw-font-bold">
                    {piece.value}
                </strong>
            );
        }
        const truncate = index === lastTextIndex;
        return (
            <span
                key={`${piece.value}-${index}`}
                className={truncate ? 'tw-min-w-0 tw-truncate' : 'tw-shrink-0 tw-whitespace-nowrap'}
            >
                {piece.value}
            </span>
        );
    });
}

function truncateLabel(node: React.ReactNode) {
    return <span className="tw-min-w-0 tw-truncate">{node}</span>;
}

function toolbarText(label: React.ReactNode, title?: string) {
    if (title) return title;
    if (typeof label === 'string' || typeof label === 'number') return String(label);
    return '';
}

interface ActionButtonProps {
    action: ToolbarAction;
    disabled: boolean;
}

const ActionButton = React.forwardRef<HTMLButtonElement, ActionButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>>(
    ({ action, disabled, className, ...props }, ref) => (
        <Button
            ref={ref}
            variant={action.variant ?? 'ghost'}
            hasIcon={Boolean(action.icon)}
            icon={action.icon}
            disabled={disabled}
            className={cn('tw-shrink-0', className)}
            {...props}
        >
            {action.label}
        </Button>
    ),
);
ActionButton.displayName = 'ToolbarActionButton';

interface MenuTriggerProps {
    action: ToolbarAction;
    disabled: boolean;
    open: boolean;
}

const MenuTrigger = React.forwardRef<HTMLButtonElement, MenuTriggerProps & React.ButtonHTMLAttributes<HTMLButtonElement>>(
    ({ action, disabled, open, className, ...props }, ref) => (
        <ButtonDropdown
            ref={ref}
            variant="ghost"
            icon={action.icon}
            titleOne={toolbarText(action.label)}
            isDisabled={disabled}
            isOpen={open}
            className={cn('tw-shrink-0', className)}
            {...props}
        />
    ),
);
MenuTrigger.displayName = 'ToolbarMenuTrigger';

interface MenuRowsProps {
    rows: ToolbarMenuRow[];
    actionsLocked: boolean;
    onSelect: () => void;
}

const MenuRows = ({ rows, actionsLocked, onSelect }: MenuRowsProps) => (
    <>
        {rows.map((row, index) => {
            const disabled = actionsLocked || Boolean(row.disabled);
            return (
                <ListAction
                    key={row.id}
                    icon={row.icon}
                    leading={row.leading}
                    title={toolbarText(row.label, row.title)}
                    showIcon={Boolean(row.icon || row.leading)}
                    isFirstAction={index === 0}
                    isDisabled={disabled}
                    role="menuitem"
                    onClick={() => {
                        if (disabled) return;
                        row.onClick();
                        onSelect();
                    }}
                />
            );
        })}
    </>
);

interface ActionControlProps {
    action: ToolbarAction;
    actionsLocked: boolean;
    inMenu?: boolean;
    isFirst?: boolean;
    open: boolean;
    placement: { side: ToolbarMenuSide; maxHeight: number };
    onOpenChange: (open: boolean) => void;
    onMenuSelect: () => void;
}

const ActionControl = ({
    action,
    actionsLocked,
    inMenu = false,
    isFirst = false,
    open,
    placement,
    onOpenChange,
    onMenuSelect,
}: ActionControlProps) => {
    const disabled = actionsLocked || Boolean(action.disabled);
    if (!action.menu) {
        if (inMenu) {
            return (
                <ListAction
                    icon={action.icon}
                    title={toolbarText(action.label)}
                    showIcon={Boolean(action.icon)}
                    isFirstAction={isFirst}
                    isDisabled={disabled}
                    role="menuitem"
                    onClick={() => {
                        if (disabled) return;
                        action.onClick?.();
                        onMenuSelect();
                    }}
                />
            );
        }
        return (
            <ActionButton
                action={action}
                disabled={disabled}
                onClick={action.onClick}
            />
        );
    }

    return (
        <DropdownMenu open={open} onOpenChange={onOpenChange} modal={false}>
            <DropdownMenuTrigger asChild disabled={disabled}>
                <MenuTrigger action={action} disabled={disabled} open={open} />
            </DropdownMenuTrigger>
            <DropdownMenuPortal>
                <DropdownMenuContent
                    side={placement.side}
                    align="end"
                    sideOffset={8}
                    avoidCollisions={false}
                    collisionPadding={20}
                    hasPadding={false}
                    hasBorder
                    style={{ maxHeight: placement.maxHeight }}
                    className={cn('tw-w-max tw-overflow-y-auto tw-p-0', action.menuClassName)}
                >
                    <MenuRows
                        rows={action.menu}
                        actionsLocked={actionsLocked}
                        onSelect={onMenuSelect}
                    />
                </DropdownMenuContent>
            </DropdownMenuPortal>
        </DropdownMenu>
    );
};

const ToolbarTable = React.forwardRef<HTMLDivElement, ToolbarTableProps>(
    (
        {
            selectedCount,
            totalCount,
            actions = [],
            onClose,
            visible,
            boundsRef,
            defaultPosition,
            position = 'fixed',
            labels,
            allSelected = false,
            overCountLimit = false,
            countLimit = DEFAULT_TOOLBAR_COUNT_LIMIT,
            className,
        },
        ref,
    ) => {
        const { t } = useTranslation();
        const [dismissed, setDismissed] = React.useState(false);
        React.useEffect(() => {
            setDismissed(false);
        }, [selectedCount, visible]);
        const isVisible = !dismissed && (visible ?? selectedCount > 0);
        const actionsLocked = selectedCount === 0;
        const nodeRef = React.useRef<HTMLDivElement>(null);
        React.useImperativeHandle(ref, () => nodeRef.current as HTMLDivElement);

        const innerRef = React.useRef<HTMLDivElement>(null);
        const dragRef = React.useRef<HTMLButtonElement>(null);
        const counterRef = React.useRef<HTMLDivElement>(null);
        const counterMeasureRef = React.useRef<HTMLDivElement>(null);
        const mainRef = React.useRef<HTMLDivElement>(null);
        const closeRef = React.useRef<HTMLDivElement>(null);
        const ellipsisMeasureRef = React.useRef<HTMLDivElement>(null);
        const menuRowMeasureRef = React.useRef<HTMLDivElement>(null);
        const actionMeasureRefs = React.useRef(new Map<string, HTMLDivElement>());

        const [bounds, setBounds] = React.useState<DraggableBounds | undefined>();
        const [toolbarMaxWidth, setToolbarMaxWidth] = React.useState<number | undefined>();
        const [inlineCount, setInlineCount] = React.useState(() =>
            Math.min(actions.length, MAX_INLINE_TOOLBAR_ACTIONS));
        const [ellipsisOpen, setEllipsisOpen] = React.useState(false);
        const [openActionMenuId, setOpenActionMenuId] = React.useState<string | null>(null);
        const [placement, setPlacement] = React.useState<{ side: ToolbarMenuSide; maxHeight: number }>({
            side: 'top',
            maxHeight: MENU_ROW_FALLBACK_PX * 15,
        });

        const inlineCountClamped = Math.min(inlineCount, actions.length, MAX_INLINE_TOOLBAR_ACTIONS);
        const inlineActions = actions.slice(0, inlineCountClamped);
        const overflowActions = actions.slice(inlineCountClamped);
        const showEllipsis = overflowActions.length > 0;

        const openAction = actions.find((action) => action.id === openActionMenuId);
        const openMenuRows = openAction?.menu?.length ?? 0;

        const closeMenus = React.useCallback(() => {
            setEllipsisOpen(false);
            setOpenActionMenuId(null);
        }, []);

        const handleClose = () => {
            setDismissed(true);
            onClose?.();
        };

        const updatePlacement = React.useCallback(() => {
            const node = nodeRef.current;
            if (!node) return;
            const rect = node.getBoundingClientRect();
            const rowCount = Math.max(ellipsisOpen ? overflowActions.length : 0, openMenuRows);
            if (rowCount === 0) {
                setPlacement((current) => current.side === 'top' ? current : { ...current, side: 'top' });
                return;
            }
            const rowHeight = menuRowMeasureRef.current?.offsetHeight || MENU_ROW_FALLBACK_PX;
            const next = resolveToolbarMenuPlacement({
                spaceAbove: rect.top,
                spaceBelow: window.innerHeight - rect.bottom,
                rowCount,
                rowHeight,
            });
            setPlacement((current) => (
                current.side === next.side && current.maxHeight === next.maxHeight ? current : next
            ));
        }, [ellipsisOpen, openMenuRows, overflowActions.length]);

        React.useLayoutEffect(() => {
            if (!isVisible) return;

            const measure = () => {
                const node = nodeRef.current;
                const inner = innerRef.current;
                if (!node || !inner) return;

                const boundsNode = boundsRef?.current ?? null;
                const parentNode = position === 'absolute'
                    ? node.offsetParent as HTMLElement | null
                    : null;
                const containerNode = boundsNode ?? parentNode;
                const containerWidth = containerNode?.clientWidth || window.innerWidth;
                const maxToolbar = Math.floor(containerWidth * 0.9);
                setToolbarMaxWidth((current) => current === maxToolbar ? current : maxToolbar);

                const widths = actions.map((action) => actionMeasureRefs.current.get(action.id)?.offsetWidth ?? 0);
                if (actions.length > 0 && widths.some((width) => width <= 0)) return;

                const ellipsisWidth = ellipsisMeasureRef.current?.offsetWidth ?? 0;
                const dragWidth = dragRef.current?.offsetWidth ?? 0;
                const counterWidth = counterMeasureRef.current?.offsetWidth
                    || counterRef.current?.scrollWidth
                    || 0;
                const closeWidth = onClose ? (closeRef.current?.offsetWidth ?? 0) : 0;
                const innerSlots = 2 + (onClose ? 1 : 0);
                const fixed = horizontalChrome(node)
                    + horizontalChrome(inner)
                    + readGap(inner) * (innerSlots - 1)
                    + dragWidth
                    + counterWidth
                    + closeWidth
                    + (actions.length > 0 ? readGap(mainRef.current) : 0);

                const nextCount = resolveInlineActionCount(
                    widths,
                    maxToolbar - fixed - 8,
                    ellipsisWidth,
                    readGap(mainRef.current),
                );
                setInlineCount((current) => current === nextCount ? current : nextCount);
            };

            measure();
            const observer = typeof ResizeObserver === 'function' ? new ResizeObserver(measure) : null;
            const boundsNode = boundsRef?.current ?? null;
            const parentNode = position === 'absolute'
                ? (nodeRef.current?.offsetParent as HTMLElement | null)
                : null;
            observer?.observe(boundsNode ?? parentNode ?? document.documentElement);
            observer?.observe(nodeRef.current as HTMLDivElement);
            window.addEventListener('resize', measure);
            return () => {
                observer?.disconnect();
                window.removeEventListener('resize', measure);
            };
        }, [actions, allSelected, boundsRef, countLimit, isVisible, onClose, overCountLimit, position, selectedCount, totalCount]);

        React.useLayoutEffect(() => {
            if (!isVisible) return;
            updatePlacement();
            window.addEventListener('resize', updatePlacement);
            window.addEventListener('scroll', updatePlacement, true);
            return () => {
                window.removeEventListener('resize', updatePlacement);
                window.removeEventListener('scroll', updatePlacement, true);
            };
        }, [isVisible, updatePlacement]);

        React.useLayoutEffect(() => {
            if (!isVisible) return;

            const computeBounds = () => {
                const node = nodeRef.current;
                if (!node) return;

                const rect = node.getBoundingClientRect();
                const containerRect = boundsRef?.current?.getBoundingClientRect();

                if (containerRect) {
                    setBounds({
                        left: containerRect.left - rect.left,
                        top: containerRect.top - rect.top,
                        right: containerRect.right - rect.right,
                        bottom: containerRect.bottom - rect.bottom,
                    });
                } else {
                    setBounds({
                        left: -rect.left,
                        top: -rect.top,
                        right: window.innerWidth - rect.right,
                        bottom: window.innerHeight - rect.bottom,
                    });
                }
            };

            computeBounds();
            window.addEventListener('resize', computeBounds);
            return () => window.removeEventListener('resize', computeBounds);
        }, [boundsRef, inlineCountClamped, isVisible, toolbarMaxWidth]);

        const formatCount = (value: number) => formatToolbarCount(
            value,
            typeof localStorage === 'undefined' ? null : localStorage.getItem(LOCAL_STORAGE.LANGUAGE_KEY),
        );

        const counter = (() => {
            const mode: ToolbarCounterMode = resolveToolbarCounterMode({ allSelected, overCountLimit });
            if (mode === 'partial' && labels?.partialSelected) {
                const partial = labels.partialSelected(selectedCount, totalCount);
                if (partial) return truncateLabel(partial);
            }
            if (mode === 'all' && labels?.allSelected) return truncateLabel(labels.allSelected(totalCount));
            if (mode === 'over-limit' && labels?.overLimitSelected) {
                return truncateLabel(labels.overLimitSelected(selectedCount, countLimit));
            }
            if (mode === 'all-entries' && labels?.allEntriesSelected) {
                return truncateLabel(labels.allEntriesSelected());
            }

            const count = formatCount(selectedCount);
            const total = formatCount(totalCount);
            const limit = formatCount(countLimit);
            if (mode === 'all-entries') {
                return truncateLabel(translateOr(t, 'TOOLBAR.ALL_ENTRIES_SELECTED', undefined, 'All entries selected'));
            }
            if (mode === 'over-limit') {
                return boldNumbers(
                    translateOr(
                        t,
                        'TOOLBAR.SELECTED_FROM_OVER_LIMIT',
                        { count, limit },
                        `${count} from over ${limit} selected`,
                    ),
                    [count, limit],
                );
            }
            if (mode === 'all') {
                return boldNumbers(
                    translateOr(t, 'TOOLBAR.ALL_SELECTED', { total }, `All ${total} selected`),
                    [total],
                );
            }
            return boldNumbers(
                translateOr(
                    t,
                    'TOOLBAR.SELECTED_COUNT_OF_TOTAL',
                    { count, total },
                    `${count} of ${total} selected`,
                ),
                [count, total],
            );
        })();

        if (!isVisible) return null;

        const positionClass = position === 'fixed' ? 'tw-fixed' : 'tw-absolute';
        const closeAriaLabel = typeof labels?.close === 'string' ? labels.close : 'Close toolbar';

        return (
            <Draggable
                nodeRef={nodeRef}
                handle={`.${DRAG_HANDLE_CLASS}`}
                bounds={bounds}
                defaultPosition={defaultPosition}
                positionOffset={{ x: '-50%', y: 0 }}
                onStart={closeMenus}
                onDrag={updatePlacement}
            >
                <div
                    ref={nodeRef}
                    role="toolbar"
                    aria-label="Bulk actions"
                    data-name="ToolbarTable"
                    style={{ maxWidth: toolbarMaxWidth }}
                    className={cn(
                        positionClass,
                        `tw-left-1/2
                         tw-bottom-6
                         tw-z-50
                         tw-flex
                         tw-w-fit
                         tw-flex-col
                         tw-items-stretch
                         tw-border
                         tw-border-solid
                         tw-border-secondary
                         tw-rounded-lg
                         tw-shadow-md
                         tw-select-none`,
                        className,
                    )}>
                    <div
                        ref={innerRef}
                        data-name="Toolbar Container"
                        style={TOOLBAR_SURFACE_STYLE}
                        className={cn(
                            `tw-flex
                             tw-w-max
                             tw-min-w-0
                             tw-max-w-full
                             tw-items-center
                             tw-h-12
                             tw-gap-1
                             tw-px-1
                             tw-bg-fill-secondary
                             tw-rounded-[calc(var(--radius)-1px)]`,
                        )}>
                        <button
                            ref={dragRef}
                            type="button"
                            aria-label="Drag toolbar"
                            data-name="Drag Icon"
                            className={cn(
                                DRAG_HANDLE_CLASS,
                                `tw-flex
                                 tw-shrink-0
                                 tw-items-center
                                 tw-justify-center
                                 tw-w-8
                                 tw-h-10
                                 tw-rounded-full
                                 tw-bg-transparent
                                 tw-border-solid
                                 tw-border-0
                                 tw-cursor-grab
                                 active:tw-cursor-grabbing
                                 focus-visible:!tw-outline-none
                                 focus-visible:tw-border-2
                                 focus-visible:!tw-border-primary-focus`,
                            )}>
                            <span className="tw-flex tw-items-center tw-justify-center tw-icon-primary tw-h-4 tw-w-4 lg:tw-h-5 lg:tw-w-5">
                                <EllipsisVerticalTwoRowsIcon className="tw-block tw-h-full tw-w-full" />
                            </span>
                        </button>

                        <div
                            ref={mainRef}
                            data-name="Container"
                            className="tw-flex tw-items-center tw-min-w-0 tw-gap-1">
                            <div
                                ref={counterRef}
                                data-name="numberation"
                                className="tw-flex tw-min-w-0 tw-shrink tw-items-center tw-gap-1 tw-overflow-hidden tw-pr-3 tw-text-t-sm lg:tw-text-t-base tw-text-t-secondary">
                                {counter}
                            </div>

                            {inlineActions.map((action) => (
                                <div key={action.id} className="tw-flex tw-shrink-0 tw-items-center tw-gap-1">
                                    <Divider />
                                    <ActionControl
                                        action={action}
                                        actionsLocked={actionsLocked}
                                        open={openActionMenuId === action.id}
                                        placement={placement}
                                        onOpenChange={(next) => {
                                            setOpenActionMenuId(next ? action.id : null);
                                            if (next) setEllipsisOpen(false);
                                        }}
                                        onMenuSelect={closeMenus}
                                    />
                                </div>
                            ))}

                            {showEllipsis && (
                                <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1">
                                    <Divider />
                                    <DropdownMenu
                                        open={ellipsisOpen}
                                        onOpenChange={(next) => {
                                            setEllipsisOpen(next);
                                            if (!next && overflowActions.some((action) => action.id === openActionMenuId)) {
                                                setOpenActionMenuId(null);
                                            }
                                            if (next && !overflowActions.some((action) => action.id === openActionMenuId)) {
                                                setOpenActionMenuId(null);
                                            }
                                        }}
                                        modal={false}
                                    >
                                        <DropdownMenuTrigger asChild>
                                            <ButtonPanel
                                                variant="primary"
                                                icon={EllipsisHorizontalIcon}
                                                isDisabled={false}
                                                aria-label="More actions"
                                                data-testid="toolbar-ellipsis"
                                                className="data-[state=open]:tw-bg-fill-primary-press"
                                            />
                                        </DropdownMenuTrigger>
                                        <DropdownMenuPortal>
                                            <DropdownMenuContent
                                                side={placement.side}
                                                align="end"
                                                sideOffset={8}
                                                avoidCollisions={false}
                                                collisionPadding={20}
                                                hasPadding={false}
                                                className="tw-w-max tw-overflow-hidden tw-border tw-border-solid tw-border-secondary tw-p-0 !tw-rounded-lg !tw-bg-fill-primary !tw-shadow-md"
                                            >
                                                <div
                                                    data-testid="toolbar-ellipsis-menu"
                                                    className="tw-flex tw-w-max tw-min-w-full tw-flex-col tw-overflow-y-auto"
                                                    style={{ maxHeight: placement.maxHeight }}
                                                >
                                                    {(placement.side === 'top' ? [...overflowActions].reverse() : overflowActions).map((action, index) => (
                                                        <ActionControl
                                                            key={action.id}
                                                            action={action}
                                                            actionsLocked={actionsLocked}
                                                            inMenu
                                                            isFirst={index === 0}
                                                            open={openActionMenuId === action.id}
                                                            placement={placement}
                                                            onOpenChange={(next) => {
                                                                setOpenActionMenuId(next ? action.id : null);
                                                            }}
                                                            onMenuSelect={closeMenus}
                                                        />
                                                    ))}
                                                </div>
                                            </DropdownMenuContent>
                                        </DropdownMenuPortal>
                                    </DropdownMenu>
                                </div>
                            )}
                        </div>

                        {onClose && (
                            <div ref={closeRef} className="tw-flex tw-shrink-0 tw-items-center tw-gap-1">
                                <Divider />
                                <ButtonPanel
                                    variant="primary"
                                    icon={XMarkIcon}
                                    onClick={handleClose}
                                    aria-label={closeAriaLabel}
                                    isDisabled={false}
                                />
                            </div>
                        )}
                    </div>

                    {typeof document !== 'undefined' && createPortal(
                        <div
                            aria-hidden
                            className="tw-pointer-events-none tw-invisible tw-fixed tw-left-0 tw-top-0 tw-flex tw-w-max tw-items-center tw-gap-1"
                        >
                            <div
                                ref={counterMeasureRef}
                                className="tw-flex tw-shrink-0 tw-items-center tw-gap-1 tw-pr-3 tw-text-t-sm lg:tw-text-t-base"
                            >
                                {counter}
                            </div>
                            {actions.map((action) => (
                                <div
                                    key={action.id}
                                    ref={(node) => {
                                        if (node) actionMeasureRefs.current.set(action.id, node);
                                        else actionMeasureRefs.current.delete(action.id);
                                    }}
                                    className="tw-flex tw-shrink-0 tw-items-center tw-gap-1"
                                >
                                    <Divider />
                                    {action.menu ? (
                                        <MenuTrigger action={action} disabled={false} open={false} tabIndex={-1} />
                                    ) : (
                                        <ActionButton action={action} disabled={false} tabIndex={-1} />
                                    )}
                                </div>
                            ))}
                            <div ref={ellipsisMeasureRef} className="tw-flex tw-shrink-0 tw-items-center tw-gap-1">
                                <Divider />
                                <ButtonPanel
                                    variant="primary"
                                    icon={EllipsisHorizontalIcon}
                                    isDisabled={false}
                                    tabIndex={-1}
                                    aria-label="More actions"
                                />
                            </div>
                            <div ref={menuRowMeasureRef} className="tw-h-9 tw-w-px lg:tw-h-10" />
                        </div>,
                        document.body,
                    )}
                </div>
            </Draggable>
        );
    },
);

ToolbarTable.displayName = 'ToolbarTable';

export { ToolbarTable };
